'use client';

import { useState, useEffect, useCallback } from 'react';
import { getChat } from '../../lib/api/meridian-api-client';
import type { ChatMessage, Chat } from '../../types';

export interface UseSimpleMessagesQueryOptions {
  apiBaseUrl?: string;
  chatId: string;
  limit?: number;
  enabled?: boolean;
}

export interface UseSimpleMessagesQueryResult {
  messages: ChatMessage[];
  chat: Chat | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Simple hook for fetching messages without TanStack Query overhead
 * 
 * Features:
 * - Direct API calls with minimal state management
 * - Manual refetch control
 * - No automatic retries or complex caching
 * - Lightweight and easy to understand
 */
export function useSimpleMessagesQuery(options: UseSimpleMessagesQueryOptions): UseSimpleMessagesQueryResult {
  const {
    apiBaseUrl = '/api/meridian',
    chatId,
    limit = 10,
    enabled = true,
  } = options;

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chat, setChat] = useState<Chat | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!enabled || !chatId) return;

    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      const response = await getChat(apiBaseUrl, chatId, { limit });

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch messages');
      }

      setMessages(response.data?.messages || []);
      setChat(response.data || null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl, chatId, limit, enabled]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  return {
    messages,
    chat,
    isLoading,
    isError,
    error,
    refetch: fetchMessages,
  };
}
